import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/api/documents/history');
      setDocuments(response.data);
    } catch (error) {
      toast.error(t('documents.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LYD'
    }).format(amount);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-full">{t('common.loading')}</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold text-gray-900">{t('documents.title')}</h1>
      <div className="mt-8">
        <div className="flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder={t('documents.searchPlaceholder')}
                  className="input max-w-xs"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('documents.documentNumber')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('documents.purpose')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('documents.client')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('documents.generatedDate')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('documents.generatedBy')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {documents
                    .filter(doc => 
                      doc.documentNumber.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((doc) => (
                      <tr key={doc.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {doc.documentNumber}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {doc.documentPurpose || "General Insurance"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {doc.client?.firstName} {doc.client?.lastName}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(doc.generationDate).toLocaleDateString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {doc.generator?.username || 'Unknown'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <button
                            onClick={() => {
                              setSelectedDoc(doc);
                              setIsDetailsOpen(true);
                            }}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            {t('documents.viewDetails')}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <Dialog
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl rounded bg-white p-6">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
              {t('documents.documentDetails')}
            </Dialog.Title>

            {selectedDoc && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium">{t('documents.documentInformation')}</h3>
                    <p>{t('documents.documentNumber')}: {selectedDoc.documentNumber}</p>
                    <p>{t('documents.purpose')}: {selectedDoc.documentPurpose || "General Insurance"}</p>
                    <p>{t('documents.generatedDate')}: {new Date(selectedDoc.generationDate).toLocaleDateString()}</p>
                  </div>

                  <div>
                    <h3 className="font-medium">{t('documents.clientInformation')}</h3>
                    <p>{t('common.name')}: {selectedDoc.clientData.firstName} {selectedDoc.clientData.lastName}</p>
                    <p>National ID: {selectedDoc.clientData.nationalId}</p>
                  </div>

                  <div className="col-span-2">
                    <h3 className="font-medium">{t('documents.financialDetails')}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <p>{t('documents.coveredAmount')}: {formatCurrency(selectedDoc.coveredAmount)}</p>
                      <p>{t('documents.netAmount')}: {formatCurrency(selectedDoc.netAmount)}</p>
                      <p>{t('documents.tax')}: {formatCurrency(selectedDoc.tax)}</p>
                      <p>{t('documents.stampDuty')}: {formatCurrency(selectedDoc.stampDuty)}</p>
                      <p>{t('documents.creationFees')}: {formatCurrency(selectedDoc.creationFees)}</p>
                      <p>{t('documents.admissionFees')}: {formatCurrency(selectedDoc.admissionFees)}</p>
                      <p className="font-medium">{t('documents.totalAmount')}: {formatCurrency(selectedDoc.totalAmount)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="btn bg-gray-100 text-gray-700 hover:bg-gray-200"
                    onClick={() => setIsDetailsOpen(false)}
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}